// Neighborhood insurance results (Oct 27, 2026) — per carrier, how the filed
// claims near a house went: approved at the first adjuster meeting ("before"),
// and first-meeting denials won on reinspection ("after").
//
// Jennifer reads it on the QC Board reinspection cards and the deal page;
// reps read it on the canvas pin sheet while knocking.
//
// Data comes from two definer RPCs (deals RLS would hide other reps' claims):
//   neighborhood_claim_outcomes(lat, lon, ...)         per carrier near a point
//   neighborhood_claim_outcomes_for_deals(uuid[], ...) each deal's own carrier
// Counts only: no homeowner names, no addresses, no money. The outcome rules
// and the carrier spellings live in migration 20261027d. Change the RPCs and
// this file together.
import { supabase } from "./supabase";

export const METERS_PER_MILE = 1609.344;

/** Below this many decided claims a result is shown greyed, as a thin sample. */
export const THIN_SAMPLE = 5;

export interface CarrierOutcomes {
  carrier: string | null;
  claims: number;
  /** Approved at the first adjuster meeting. */
  approved_first: number;
  /** Partly approved at the first meeting (counted as approved below). */
  partial_first: number;
  /** Denied at the first meeting = won_on_reinspection + reinspection_open + denied. */
  denied_first: number;
  won_on_reinspection: number;
  reinspection_open: number;
  /** Denied first and ended there. */
  denied: number;
  /** No decision yet. */
  pending: number;
  /** Closed before any decision. */
  lost: number;
}

export interface DealNeighborhoodOutcomes extends CarrierOutcomes {
  deal_id: string;
  no_location: boolean;
  radius_m: number;
  widened: boolean;
}

export interface PointNeighborhoodOutcomes {
  radius_m: number;
  widened: boolean;
  /** Claims nearby with no carrier on record (left out of every carrier). */
  unrecorded: number;
  carriers: CarrierOutcomes[];
}

const n = (v: unknown): number => {
  const x = Number(v);
  return Number.isFinite(x) ? x : 0;
};

export function coerceOutcomes(raw: Record<string, unknown>): CarrierOutcomes {
  return {
    carrier: typeof raw.carrier === "string" && raw.carrier ? raw.carrier : null,
    claims: n(raw.claims),
    approved_first: n(raw.approved_first),
    partial_first: n(raw.partial_first),
    denied_first: n(raw.denied_first),
    won_on_reinspection: n(raw.won_on_reinspection),
    reinspection_open: n(raw.reinspection_open),
    denied: n(raw.denied),
    pending: n(raw.pending),
    lost: n(raw.lost),
  };
}

/** Claims the first adjuster meeting decided (approved, partial or denied). */
export function decidedFirst(o: CarrierOutcomes): number {
  return o.approved_first + o.partial_first + o.denied_first;
}

/** Approved at the first meeting, partials included. */
export function approvedFirst(o: CarrierOutcomes): number {
  return o.approved_first + o.partial_first;
}

export function isThin(o: CarrierOutcomes): boolean {
  return decidedFirst(o) < THIN_SAMPLE;
}

/** Whole-number percent, or null when there is nothing to divide by. */
export function pct(part: number, whole: number): number | null {
  if (!whole) return null;
  return Math.round((part / whole) * 100);
}

/** "1 mi", "3 mi", "0.5 mi". */
export function radiusLabel(m: number): string {
  const mi = m / METERS_PER_MILE;
  const r = Math.round(mi * 10) / 10;
  return `${Number.isInteger(r) ? r.toFixed(0) : r} mi`;
}

/** "4 of 15 approved at the first meeting (1 partial)" — or null when none decided. */
export function beforeText(o: CarrierOutcomes): string | null {
  const d = decidedFirst(o);
  if (!d) return null;
  const partial = o.partial_first ? ` (${o.partial_first} partial)` : "";
  return `${approvedFirst(o)} of ${d} approved at the first meeting${partial}`;
}

/** "2 of 11 denials won on reinspection · 1 still open" — or null when nothing was denied. */
export function afterText(o: CarrierOutcomes): string | null {
  if (!o.denied_first) return null;
  const open = o.reinspection_open ? ` · ${o.reinspection_open} still in reinspection` : "";
  return `${o.won_on_reinspection} of ${o.denied_first} denial${o.denied_first === 1 ? "" : "s"} won on reinspection${open}`;
}

/** Sort carriers busiest first; ties by name. */
export function sortCarriers(list: CarrierOutcomes[]): CarrierOutcomes[] {
  return [...list].sort((a, b) => b.claims - a.claims || (a.carrier ?? "").localeCompare(b.carrier ?? ""));
}

export async function fetchDealNeighborhoodOutcomes(
  dealIds: string[],
): Promise<Record<string, DealNeighborhoodOutcomes>> {
  const ids = Array.from(new Set(dealIds.filter(Boolean)));
  if (!ids.length) return {};
  const out: Record<string, DealNeighborhoodOutcomes> = {};
  // The RPC takes at most 300 ids per call.
  for (let i = 0; i < ids.length; i += 300) {
    const { data, error } = await supabase.rpc("neighborhood_claim_outcomes_for_deals", {
      p_deal_ids: ids.slice(i, i + 300),
    });
    if (error) throw new Error(error.message);
    for (const raw of (Array.isArray(data) ? data : []) as Record<string, unknown>[]) {
      const id = String(raw.deal_id ?? "");
      if (!id) continue;
      out[id] = {
        ...coerceOutcomes(raw),
        deal_id: id,
        no_location: raw.no_location === true,
        radius_m: n(raw.radius_m) || METERS_PER_MILE,
        widened: raw.widened === true,
      };
    }
  }
  return out;
}

export async function fetchPointNeighborhoodOutcomes(
  lat: number,
  lon: number,
  excludeDealId?: string | null,
): Promise<PointNeighborhoodOutcomes> {
  const { data, error } = await supabase.rpc("neighborhood_claim_outcomes", {
    p_lat: lat,
    p_lon: lon,
    p_exclude_deal_id: excludeDealId ?? null,
  });
  if (error) throw new Error(error.message);
  const raw = (data ?? {}) as Record<string, unknown>;
  const carriers = Array.isArray(raw.carriers)
    ? (raw.carriers as Record<string, unknown>[]).map(coerceOutcomes).filter((c) => c.carrier)
    : [];
  return {
    radius_m: n(raw.radius_m) || METERS_PER_MILE,
    widened: raw.widened === true,
    unrecorded: n(raw.unrecorded),
    carriers: sortCarriers(carriers),
  };
}
